import cx from "classnames";
import { Fragment, ReactNode, useEffect, useState } from "react";
import { useCarousel } from "../../../hooks";
import EmbeddedAction from "../../Block/EmbeddedAction/EmbeddedAction";
import Icon from "../../Element/Icon/icon";
import ResponsiveIframe from "../../Element/ResponsiveIframe";
import ResponsiveImage from "../../Element/ResponsiveImage";
import { insertImuAt } from "../../Utility/Items/Items";
import { ImageCrop } from "../../Utility/dataTypes";
import styles from "./embeddedgallery.module.css";

type EmbeddedAction = React.ComponentProps<typeof EmbeddedAction>;
type Props = {
  items: GalleryItem[];
  srcWidth: number;
  srcHeight: number;
  leftIcon: ReactNode;
  rightIcon: ReactNode;
  actionbar: {
    shareIcon: EmbeddedAction["shareIcon"];
    onFullScreen: (goto: number, action: string) => void;
    fullScreenIcon: EmbeddedAction["fullScreenIcon"];
  };
  ImuInsertAt: number;
  Imu: ReactNode;
  slidePosition?: number;
  shareMedia?: string[];
  onSlideChange?: (arg0: number, arg1: string, arg2: string) => void;
  onSlideClick?: (arg0: number, arg1: string, arg2: string) => void;
  errorSrc?: string;
};

type GalleryItem = {
  title: ReactNode;
  description?: ReactNode;
  caption: EmbeddedAction["caption"];
  type?: string;
  position?: number;
  videoUrl?: string;
  image?: ImageCrop;
  shareUrl?: string;
  appendedGalleryItem?: ReactNode;
};

/**
 * TODO: Can replace with NaturalCarousel for future refactor
 */
export default function EmbeddedGallery({
  items,
  srcWidth,
  srcHeight,
  leftIcon,
  rightIcon,
  Imu,
  ImuInsertAt = 2,
  actionbar,
  slidePosition,
  shareMedia,
  onSlideChange = () => {},
  onSlideClick = () => {},
  errorSrc,
}: Props): ReactNode {
  // create a new array with ads at the insertAt position.
  const itemsWithImu = insertImuAt(items, ImuInsertAt);
  // position with ads based on URL slide position
  const carouselPosition = itemsWithImu.findIndex(
    (item) => item.position === slidePosition,
  );

  const {
    next,
    prev,
    position,
    hasReachedEnd,
    hasSlideMoved,
    refPassthrough,
    TouchHandlers,
    goto,
  } = useCarousel(itemsWithImu.length, 1);
  const [isAds, setIsAds] = useState(false);

  const getGalleryId = (event: React.MouseEvent<HTMLElement>) => {
    const eGalleryId = event.currentTarget
      .closest("[data-gallery-id]")
      ?.getAttribute("data-gallery-id");
    return eGalleryId ?? "";
  };

  //check if the current item is an ad
  useEffect(() => {
    if (itemsWithImu[position]?.type === "ads") {
      setIsAds(true);
    } else {
      setIsAds(false);
    }
  }, [itemsWithImu, position]);

  useEffect(() => {
    // carouselPosition will be -1 if not found
    if (carouselPosition > 0) {
      goto(carouselPosition);
    }
  }, [carouselPosition, goto]);

  const galleryContainerNavigation = (
    <div className={styles.navigation}>
      {hasSlideMoved && (
        <button
          className={styles.prev}
          onClick={(event) => {
            prev();
            onSlideChange(position - 1, "prev", getGalleryId(event));
          }}
        >
          <Icon>{leftIcon}</Icon>
        </button>
      )}

      {!hasReachedEnd && (
        <button
          className={styles.next}
          onClick={(event) => {
            next();
            onSlideChange(position + 1, "next", getGalleryId(event));
          }}
        >
          <Icon>{rightIcon}</Icon>
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={cx(isAds && styles.hidden)}>
        {itemsWithImu[position]?.title}
      </div>

      <div className={styles.carouselWrapper}>
        <div
          className={styles.embeddedgalleryContainer}
          style={{ aspectRatio: srcWidth / srcHeight }}
          {...TouchHandlers}
          ref={refPassthrough}
        >
          {itemsWithImu?.map((item, index) => (
            <div
              key={index}
              className={styles.embeddedgalleryItemContainer}
              style={{ aspectRatio: srcWidth / srcHeight }}
              onClick={(event) => {
                onSlideClick(position, "click", getGalleryId(event));
              }}
            >
              {item.type === "ads" ? (
                <Fragment>{Imu}</Fragment>
              ) : (
                <>
                  {item.image && (
                    <ResponsiveImage
                      {...item.image}
                      errorSrc={errorSrc}
                      fill="contain"
                      displayWidth={srcWidth}
                    />
                  )}
                  {item.videoUrl && (
                    <div
                      style={{
                        width:
                          item.videoUrl.includes("instagram") ||
                          item.videoUrl.includes("tiktok") ||
                          item.videoUrl.includes("youtube.com/shorts")
                            ? "fit-content"
                            : // avoid overlap of e.g. youtube interface with prev and next arrow
                              "calc(100% - 100px)",
                      }}
                    >
                      <ResponsiveIframe src={item.videoUrl} loading="lazy" />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {galleryContainerNavigation}
      </div>

      <div className={cx(styles.actionBar, isAds && styles.hidden)}>
        <EmbeddedAction
          caption={itemsWithImu[position]?.caption}
          pageinationLabel={`${itemsWithImu[position]?.position ?? ""}/${
            items.length
          }`}
          shareIcon={actionbar.shareIcon}
          onFullScreen={() =>
            actionbar.onFullScreen(
              itemsWithImu[position]?.position || 0,
              "click",
            )
          }
          fullScreenIcon={actionbar.fullScreenIcon}
          shareUrl={
            itemsWithImu[position]?.shareUrl && itemsWithImu[position]?.position
              ? `${itemsWithImu[position].shareUrl}&eslide=${itemsWithImu[position].position}`
              : ""
          }
          shareMedia={shareMedia}
        />
      </div>

      <div className={cx(isAds && styles.hidden)}>
        {itemsWithImu[position]?.description}
        {itemsWithImu[position]?.appendedGalleryItem}
      </div>
    </div>
  );
}
